// frontend/src/auth/canonicalWorkspaceSwitch.ts
//
// CRM's transport for Central's canonical workspace switch
// (`POST /api/suite/switch-workspace`, Central PR #837).
//
// WHAT THIS REPLACES
// ------------------
// `beginRpWorkspaceTransition` sent the user to CRM's OWN
// `/auth/start?workspaceId=…&returnTo=…`. That is only correct when the
// destination workspace entitles CRM. When it does not — a personal
// workspace being the everyday case — CRM authenticated successfully and
// then refused, and ui-shell's access guard bounced the user onward using
// `home.launchUrl ?? home.webUrl` read out of the browser's `apps[]`.
//
// Three things were wrong with that: CRM decided a destination it does not
// own, the Central session moved before anything knew the destination was
// reachable, and the originating route was carried into a different app.
//
// Central now resolves the destination app and moves the session atomically,
// so CRM's job is reduced to carrying the request and handing back the
// answer unchanged.
//
// WHY A DIRECT BROWSER CALL AND NOT A BFF PROXY
// ---------------------------------------------
// `/api/suite/switch-workspace` authenticates with Central's `central_sid`
// cookie and has no Bearer path. CRM's BFF holds `crm_sid` and Central
// tokens — it does NOT hold the user's Central session cookie, so it cannot
// make this call on the user's behalf.
//
// The browser can. `crm-staging.roilevelup.com` and
// `central-staging.roilevelup.com` share the registrable domain
// `roilevelup.com`, so the request is same-SITE even though it is
// cross-ORIGIN, and `SameSite=Lax` sends the cookie on it. Verified live
// from the CRM origin: 200 with a resolved destination.
//
// This is a PIPE, not a decision point. It returns Central's answer verbatim
// and never substitutes a destination of its own.

import type { CanonicalSwitchDecision } from '@levelup/ui-shell';
import { CENTRAL_URL } from '../config/centralAuth';

/** Network/parse failures are refusals — never a reason to guess a destination. */
const TRANSPORT_FAILURE: CanonicalSwitchDecision = { ok: false, reason: 'central-unreachable' };

export async function requestCanonicalWorkspaceSwitch(
  workspaceId: string,
): Promise<CanonicalSwitchDecision> {
  const base = (CENTRAL_URL || '').replace(/\/+$/, '');
  if (!base) {
    // Without Central's origin there is no authority to ask. Fail closed
    // rather than falling back to a CRM-constructed URL.
    return { ok: false, reason: 'central-url-not-configured' };
  }

  let res: Response;
  try {
    res = await fetch(`${base}/api/suite/switch-workspace`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ workspaceId }),
    });
  } catch {
    return TRANSPORT_FAILURE;
  }

  try {
    const body = (await res.json()) as CanonicalSwitchDecision;
    // Returned VERBATIM. CRM does not repair, re-resolve or re-shape
    // Central's answer — a malformed success is handled by ui-shell's
    // fail-closed check, not by inventing a destination here.
    return body;
  } catch {
    return TRANSPORT_FAILURE;
  }
}
